import { ChevronDownIcon } from '@chakra-ui/icons';
import type { ReactNode } from 'react';
import { PlatformIcon } from '@/components/PlatformIcon';
import type { PlatformTypes } from '@/types/gql.generated';
import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  type MenuProps,
} from '@/ui';
import { getName } from '@/utils/platformLib';

type Props = {
  /** List of platforms to show in the dropdown */
  platforms: PlatformTypes[];
  /** Currently selected platform */
  value: PlatformTypes | null;
  /** Called when a platform is selected */
  onChange: (platform: PlatformTypes) => void;
  /** Placeholder text when no platform selected */
  placeholder?: string;
  /** Optional render function for additional content per menu item */
  renderItemExtra?: (platform: PlatformTypes) => ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Menu placement */
  placement?: MenuProps['placement'];
  /** Controlled open state */
  isOpen?: boolean;
  /** Called when menu opens */
  onOpen?: () => void;
  /** Called when menu closes */
  onClose?: () => void;
  /** Match button width for menu list */
  matchWidth?: boolean;
};

export const PlatformSelect = ({
  platforms,
  value,
  onChange,
  placeholder = 'Select platform',
  renderItemExtra,
  isLoading,
  placement = 'bottom-start',
  isOpen,
  onOpen,
  onClose,
  matchWidth,
}: Props) => {
  return (
    <Menu
      isLazy
      isOpen={isOpen}
      matchWidth={matchWidth}
      placement={placement}
      onClose={onClose}
      onOpen={onOpen}
    >
      <MenuButton
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        disabled={isLoading}
        flexGrow="1"
        overflow="hidden"
        pl="3"
        pr="2"
        py="2"
        type="button"
        w="100%"
      >
        <Flex align="center" as="span" justify="space-between">
          <Flex
            align="center"
            as="span"
            flexShrink="1"
            gap="3"
            justify="flex-start"
            minW="0"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {value && <PlatformIcon platform={value} />}
            <Text as="span">{value ? getName(value) : placeholder}</Text>
          </Flex>
          <ChevronDownIcon boxSize="20px" />
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList borderRadius="lg" data-control zIndex="popover">
          {platforms.map((platform) => (
            <MenuItem
              icon={<PlatformIcon platform={platform} />}
              key={platform}
              onClick={() => onChange(platform)}
            >
              <Flex align="center" as="span" justify="space-between" w="100%">
                <Text as="span">{getName(platform)}</Text>
                {renderItemExtra?.(platform)}
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};
